import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {Dialog, Transition} from "@headlessui/react";
import {BellIcon, RefreshIcon} from "@heroicons/react/outline";
import NumberFormat from "react-number-format";

import LoaderWrapper from "../../../components/common/loader/loader-wrapper";
import UserInitial from "../../../components/common/table/user-initial";
import {getFormattedDate} from "../../../utils/timeUtils";
import {fetchDocumentRequestEvents} from "../services/document-service";
import {
    extractRequestEvents,
    isPendingRequestEvent,
    sanitizeRequestEvent,
} from "../utils/request-event-utils";

const resolveStatusBadge = (status, t) => {
    const normalized = typeof status === "string" ? status.trim().toLowerCase() : "";
    if (normalized === "confirmed") {
        return {
            label: t("document.request_events.status_confirmed", {defaultValue: "Confermata"}),
            className: "bg-emerald-100 text-emerald-800 border border-emerald-200",
        };
    }
    if (normalized === "rejected") {
        return {
            label: t("document.request_events.status_rejected", {defaultValue: "Rifiutata"}),
            className: "bg-rose-100 text-rose-800 border border-rose-200",
        };
    }
    return {
        label: t("document.request_events.status_pending", {defaultValue: "In attesa"}),
        className: "bg-amber-100 text-amber-800 border border-amber-200",
    };
};

const formatBadgeValue = (value) => {
    if (!Number.isFinite(value) || value <= 0) {
        return "0";
    }
    if (value > 99) {
        return "99+";
    }
    if (value > 9) {
        return "9+";
    }
    return String(value);
};

const DocumentRequestEventsButton = ({companyId, documentId, onSelectEvent, refreshKey}) => {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [counts, setCounts] = useState({pending: 0, total: 0});
    const [lastError, setLastError] = useState(null);

    const hasIdentifiers = Boolean(companyId && documentId);

    const orderedEvents = useMemo(() => {
        if (!Array.isArray(events) || events.length === 0) {
            return [];
        }
        return [...events].sort((a, b) => {
            const left = a?.eventDate ?? "";
            const right = b?.eventDate ?? "";
            if (!left && !right) {
                return 0;
            }
            if (!left) {
                return 1;
            }
            if (!right) {
                return -1;
            }
            return right.localeCompare(left);
        });
    }, [events]);

    const loadEvents = useCallback(async ({silent = false} = {}) => {
        if (!hasIdentifiers) {
            setEvents([]);
            setCounts({pending: 0, total: 0});
            return;
        }
        if (!silent) {
            setIsLoading(true);
        }
        try {
            const response = await fetchDocumentRequestEvents({
                companyId,
                documentId,
            });
            const rawEvents = extractRequestEvents(response);
            const sanitized = rawEvents
                .map((entry) => sanitizeRequestEvent(entry))
                .filter(Boolean);
            setEvents(sanitized);
            const pendingCount = typeof response?.not_confirmed_rows === "number"
                ? Math.max(response.not_confirmed_rows, 0)
                : sanitized.filter((item) => isPendingRequestEvent(item)).length;
            const totalCount = typeof response?.total_rows === "number"
                ? response.total_rows
                : sanitized.length;
            setCounts({pending: pendingCount, total: totalCount});
            setLastError(null);
        } catch (error) {
            if (!silent) {
                toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            }
            setLastError(error);
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    }, [companyId, documentId, hasIdentifiers, t]);

    useEffect(() => {
        if (!hasIdentifiers) {
            setEvents([]);
            setCounts({pending: 0, total: 0});
            return;
        }
        loadEvents({silent: true});
    }, [hasIdentifiers, loadEvents, refreshKey]);

    const handleOpen = useCallback(() => {
        if (!hasIdentifiers) {
            return;
        }
        setIsOpen(true);
        loadEvents();
    }, [hasIdentifiers, loadEvents]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleRefresh = useCallback(() => {
        loadEvents();
    }, [loadEvents]);

    const handleSelectEvent = useCallback((eventItem) => {
        if (typeof onSelectEvent === "function") {
            onSelectEvent(eventItem);
        }
        setIsOpen(false);
    }, [onSelectEvent]);

    const buttonLabel = useMemo(() => (
        t("document.request_events.button_label", {defaultValue: "Richieste"})
    ), [t]);

    const modalTitle = useMemo(() => (
        t("document.request_events.title", {defaultValue: "Richieste di conferma"})
    ), [t]);

    const modalSubtitle = useMemo(() => (
        t("document.request_events.subtitle", {defaultValue: "Visualizza e gestisci le richieste provenienti dalle righe del documento."})
    ), [t]);

    const emptyMessage = useMemo(() => (
        t("document.request_events.empty", {defaultValue: "Al momento non sono presenti richieste da mostrare."})
    ), [t]);

    const manageButtonLabel = useMemo(() => (
        t("document.request_events.manage_action", {defaultValue: "Gestisci riga"})
    ), [t]);

    const refreshLabel = useMemo(() => (
        t("document.request_events.refresh_action", {defaultValue: "Aggiorna"})
    ), [t]);

    const pendingLabel = useMemo(() => (
        t("document.request_events.pending_count", {
            defaultValue: "Richieste in attesa: {{count}}",
            count: counts.pending,
        })
    ), [counts.pending, t]);

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                disabled={!hasIdentifiers}
                className={`relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-600 shadow-sm transition hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 ${hasIdentifiers ? "" : "cursor-not-allowed opacity-50"}`}
                aria-label={buttonLabel}
                title={buttonLabel}
            >
                <BellIcon className="h-5 w-5" aria-hidden="true" />
                {counts.pending > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-tight text-white">
                        {formatBadgeValue(counts.pending)}
                    </span>
                )}
            </button>

            <Transition show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-[120] overflow-y-auto"
                    onClose={handleClose}
                >
                    <div className="flex min-h-full items-end justify-center px-4 py-8 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay className="fixed inset-0 bg-gray-900/30" />
                        </Transition.Child>

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full transform overflow-hidden rounded-xl bg-white px-4 pb-5 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Dialog.Title className="text-xl font-semibold text-gray-900">
                                                {modalTitle}
                                            </Dialog.Title>
                                            <p className="mt-1 text-sm text-gray-500">
                                                {modalSubtitle}
                                            </p>
                                            <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                {pendingLabel}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRefresh}
                                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700 shadow-sm transition hover:bg-gray-50"
                                        >
                                            <RefreshIcon className="h-4 w-4" aria-hidden="true" />
                                            {refreshLabel}
                                        </button>
                                    </div>

                                    <LoaderWrapper isLoading={isLoading}>
                                        {orderedEvents.length === 0 ? (
                                            <div className="rounded-lg border border-dashed border-gray-200 p-5 text-center">
                                                <p className="text-sm text-gray-500">
                                                    {emptyMessage}
                                                </p>
                                                {lastError && (
                                                    <p className="mt-2 text-xs text-rose-600">
                                                        {lastError.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"})}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
                                                {orderedEvents.map((eventItem, index) => {
                                                    const key = eventItem.id ?? `event-${index}`;
                                                    const statusBadge = resolveStatusBadge(eventItem.status, t);
                                                    const formattedDate = eventItem.eventDate
                                                        ? getFormattedDate(eventItem.eventDate)
                                                        : "-";
                                                    const productName = eventItem.product?.name
                                                        ?? t("document.request_events.unknown_product", {defaultValue: "Prodotto non disponibile"});
                                                    const productCode = eventItem.product?.code ?? "";
                                                    const user = eventItem.user ?? null;
                                                    const fallbackUserName = user?.fullname
                                                        ?? t("app.common.unknown_user", {defaultValue: "Utente sconosciuto"});
                                                    const unitSymbol = eventItem.unitSymbol
                                                        ? ` ${eventItem.unitSymbol}`
                                                        : "";
                                                    return (
                                                        <li key={key} className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                                                            <div className="flex flex-1 items-start gap-3">
                                                                <UserInitial user={user ?? {fullname: fallbackUserName}} />
                                                                <div className="space-y-1">
                                                                    <p className="text-sm font-semibold text-gray-900">
                                                                        {productName}
                                                                    </p>
                                                                    {productCode && (
                                                                        <p className="text-xs text-gray-500">
                                                                            {productCode}
                                                                        </p>
                                                                    )}
                                                                    <p className="text-xs text-gray-500">
                                                                        {formattedDate}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500">
                                                                        {fallbackUserName}
                                                                    </p>
                                                                    <span className={`inline-flex w-auto items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusBadge.className}`}>
                                                                        {statusBadge.label}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-center sm:gap-4">
                                                                <div className="text-right text-base font-semibold text-gray-900">
                                                                    <NumberFormat
                                                                        value={eventItem.quantity}
                                                                        displayType="text"
                                                                        thousandSeparator="."
                                                                        decimalSeparator=","
                                                                        decimalScale={4}
                                                                        suffix={unitSymbol}
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectEvent(eventItem)}
                                                                    className="inline-flex items-center justify-center rounded-md border border-blue-500 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-blue-600 transition hover:bg-blue-50"
                                                                >
                                                                    {manageButtonLabel}
                                                                </button>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </LoaderWrapper>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};

DocumentRequestEventsButton.propTypes = {
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectEvent: PropTypes.func,
    refreshKey: PropTypes.number,
};

DocumentRequestEventsButton.defaultProps = {
    companyId: null,
    documentId: null,
    onSelectEvent: undefined,
    refreshKey: 0,
};

export default DocumentRequestEventsButton;
